
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { BeforeAfterCase } from '@/types/gallery';
import { Eye, EyeOff, Pencil, Trash2, Calendar, ExternalLink, Copy, Maximize } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { createDoctorDeepLink } from '@/utils/deepLinkUtils';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface GalleryGridProps {
  cases: BeforeAfterCase[];
  onEdit: (caseItem: BeforeAfterCase) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string) => void;
}

const GalleryGrid: React.FC<GalleryGridProps> = ({ 
  cases, 
  onEdit, 
  onDelete, 
  onToggleVisibility 
}) => {
  const { toast } = useToast();
  const [lightboxImage, setLightboxImage] = useState<{url: string, title: string} | null>(null);
  
  if (cases.length === 0) {
    return (
      <div className="bg-muted/30 rounded-lg text-center p-12 my-6">
        <h3 className="text-lg font-medium mb-2">No cases found</h3>
        <p className="text-muted-foreground">
          Add your first case by clicking the "Add New Case" button above.
        </p>
      </div>
    );
  }

  const copyDeepLink = (caseId: string) => {
    // Create a deep link for the specific case
    const deepLink = `${window.location.origin}/doctor-profile/1?case=${caseId}`;
    navigator.clipboard.writeText(deepLink)
      .then(() => {
        toast({
          title: "Link copied!",
          description: "Case link has been copied to clipboard",
        });
      })
      .catch(() => {
        toast({
          title: "Failed to copy",
          description: "Could not copy the link to clipboard",
          variant: "destructive"
        });
      });
  };

  const openLightbox = (imageUrl: string, title: string) => {
    setLightboxImage({ url: imageUrl, title });
  };

  const viewOnProfile = (caseId: string) => {
    window.open(`/doctor-profile/1?case=${caseId}`, '_blank');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
      {cases.map((caseItem) => (
        <Card key={caseItem.id} className="overflow-hidden hover:shadow-md transition-shadow">
          <div className="grid grid-cols-2 gap-1 p-1">
            <div className="relative">
              <img 
                src={caseItem.beforeImage} 
                alt={`Before: ${caseItem.title}`} 
                className="h-36 w-full object-cover rounded-tl-md cursor-pointer"
                onClick={() => openLightbox(caseItem.beforeImage, `Before: ${caseItem.title}`)}
              />
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 left-2 h-6 w-6 rounded-full bg-black/70 text-white hover:bg-black/90"
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox(caseItem.beforeImage, `Before: ${caseItem.title}`);
                }}
              >
                <Maximize className="h-3 w-3" />
              </Button>
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                Before
              </div>
            </div>
            <div className="relative">
              <img 
                src={caseItem.afterImage} 
                alt={`After: ${caseItem.title}`} 
                className="h-36 w-full object-cover rounded-tr-md cursor-pointer"
                onClick={() => openLightbox(caseItem.afterImage, `After: ${caseItem.title}`)}
              />
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/70 text-white hover:bg-black/90"
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox(caseItem.afterImage, `After: ${caseItem.title}`);
                }}
              >
                <Maximize className="h-3 w-3" />
              </Button>
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                After
              </div>
            </div>
          </div>
          
          <CardHeader className="p-4 pb-0">
            <div className="flex justify-between items-center">
              <h3 className="font-medium line-clamp-1">{caseItem.title}</h3>
              <div className="flex items-center">
                {caseItem.isPublic ? <Eye className="h-4 w-4 text-primary mr-1" /> : <EyeOff className="h-4 w-4 text-muted-foreground mr-1" />}
                <span className="text-xs">{caseItem.isPublic ? 'Public' : 'Private'}</span>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-4 pt-2">
            <div className="text-sm text-muted-foreground">
              {caseItem.clinicName}
            </div>
            {caseItem.treatmentType && (
              <div className="mt-1 text-xs inline-flex items-center bg-muted px-2 py-1 rounded">
                {caseItem.treatmentType}
              </div>
            )}
            <div className="flex items-center mt-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3 mr-1" />
              Updated {formatDistanceToNow(caseItem.updatedAt, { addSuffix: true })}
            </div>

            {/* Only show these options for public cases */}
            {caseItem.isPublic && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => copyDeepLink(caseItem.id)}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => viewOnProfile(caseItem.id)}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View on Profile
                </Button>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="p-4 pt-0 flex justify-between">
            <div className="flex items-center">
              <Switch 
                checked={caseItem.isPublic} 
                onCheckedChange={() => onToggleVisibility(caseItem.id)}
                id={`visibility-${caseItem.id}`}
              />
              <label 
                htmlFor={`visibility-${caseItem.id}`}
                className="ml-2 text-sm cursor-pointer"
              >
                {caseItem.isPublic ? 'Public' : 'Private'}
              </label>
            </div>
            
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onEdit(caseItem)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this case from your gallery. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDelete(caseItem.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardFooter>
        </Card>
      ))}

      {/* Lightbox Modal */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => !open && setLightboxImage(null)}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-black/95 border-none">
          <div className="relative p-0">
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-2 right-2 text-white hover:bg-white/20 z-10"
              onClick={() => setLightboxImage(null)}
            >
              ✕
            </Button>
            {lightboxImage && (
              <div className="flex flex-col items-center justify-center p-4">
                <img 
                  src={lightboxImage.url} 
                  alt={lightboxImage.title} 
                  className="max-h-[80vh] w-auto object-contain" 
                />
                <div className="text-white text-sm mt-4">{lightboxImage.title}</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryGrid;
